import React, { useState, useEffect } from 'react';
import './AppointmentDetailsModal.css';
import { getPaymentByAppointmentApi } from '../services/apiService';
import PaymentDetailsTable from './consultation/common/PaymentDetailsTable';
import AddPaymentModal from './consultation/modals/AddPaymentModal';
import AddServiceModal from './consultation/modals/AddServiceModal';
import { CheckCircle, AlertCircle } from 'lucide-react';
import { 
  getServicesApi, 
  addServiceToPaymentApi, 
  recordPaymentApi 
} from '../services/apiService';

const AppointmentDetailsModal = ({ 
  isOpen, 
  onClose, 
  appointment, 
  activeTab, 
  setActiveTab,
  userType 
}) => {
  const [paymentData, setPaymentData] = useState(null);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentError, setPaymentError] = useState(null);
  const [isAppointmentPast, setIsAppointmentPast] = useState(false);
  const [showAddPaymentModal, setShowAddPaymentModal] = useState(false);
  const [showAddServiceModal, setShowAddServiceModal] = useState(false);
  const [serviceCatalog, setServiceCatalog] = useState([]);

  activeTab = 'payments';

  // Check if appointment date has passed
  useEffect(() => {
    if (appointment?.date) {
      const appointmentDate = new Date(appointment.date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      appointmentDate.setHours(0, 0, 0, 0);
      setIsAppointmentPast(appointmentDate < today);
    }
  }, [appointment?.date]);

  // Fetch service catalog
  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await getServicesApi();
        const servicesData = response.data.data.services || response.data.data || [];
        const services = servicesData.map(service => ({
          id: service.id,
          name: service.nom || service.name || '',
          category: service.type || service.category || '',
          defaultQuantity: parseInt(service.qte || 1),
          defaultTotal: parseFloat(service.prix || service.price || 0),
          unit: 'unité'
        }));
        setServiceCatalog(services);
      } catch (error) {
        console.error('Error fetching service catalog:', error);
      }
    };
    
    if (isOpen && userType === "Doctor") {
      fetchServices();
    }
  }, [isOpen, userType]);

  // Fetch payment data when modal opens or appointment changes
  useEffect(() => {
    if (isOpen && appointment?.id) {
      fetchPaymentData();
    }
  }, [isOpen, appointment?.id]);

  const fetchPaymentData = async () => {
    try {
      setPaymentLoading(true);
      setPaymentError(null);
      
      const response = await getPaymentByAppointmentApi(appointment.id);
      const payment = response.data.data || response.data;

      if (payment) {
        // Transform API structure to PaymentDetailsTable format
        const transformedPayment = {
          id: payment.id,
          appointmentId: `RDV-${appointment.id}`,
          appointmentDate: appointment.date || new Date().toISOString(),
          totalAmount: payment.total_amount || 0,
          paidAmount: payment.paid_amount || 0,
          debt: payment.debt_amount || 0,
          status: payment.status || 'unpaid',
          services: (payment.services || []).map(service => ({
            id: service.id,
            serviceId: service.service_id,
            name: service.name,
            quantity: service.quantity,
            unitPrice: service.price,
            total: service.total
          })),
          paymentHistory: (payment.payment_history || []).map(hist => ({
            id: hist.id,
            amount: hist.amount,
            date: hist.paid_at || hist.recorded_at,
            method: hist.payment_method,
            paymentType: hist.payment_type,
            note: hist.note || '',
            recordedBy: hist.recorded_by?.name || 'N/A'
          })),
          notes: payment.notes || ''
        };
        
        setPaymentData(transformedPayment);
      } else {
        setPaymentData(null);
      }
    } catch (error) {
      console.error('Error fetching payment:', error);
      setPaymentError(error.response?.data?.message || 'Failed to load payment information');
    } finally {
      setPaymentLoading(false);
    }
  };

  const handleAddService = async (serviceData) => {
    try {
      const payload = {
        service_id: serviceData.service.id,
        quantity: serviceData.quantity,
        total_price: serviceData.total
      };

      await addServiceToPaymentApi(paymentData.id, payload);
      
      // Refresh payment data
      await fetchPaymentData();
      setShowAddServiceModal(false);
    } catch (error) {
      console.error('Error adding service:', error);
      alert(error.response?.data?.message || 'Erreur lors de l\'ajout du service');
    }
  };

  const handleAddPayment = async (paymentInfo) => {
    try {
      const methodMap = {
        'Espèces': 'cash',
        'Carte bancaire': 'card',
        'Chèque': 'check',
        'Virement': 'transfer',
        'Mobile Money': 'mobile',
        'Annulation': 'cancellation'
      };

      const payload = {
        amount: paymentInfo.amount,
        payment_method: methodMap[paymentInfo.paymentMethod] || 'cash',
        notes: paymentInfo.note || ''
      };

      await recordPaymentApi(paymentData.id, payload);
      
      // Refresh payment data
      await fetchPaymentData();
      setShowAddPaymentModal(false);
    } catch (error) {
      console.error('Error adding payment:', error);
      alert(error.response?.data?.message || 'Erreur lors de l\'enregistrement du paiement');
    }
  };

  if (!isOpen || !appointment) return null;

  return (
    <div className="appointment-modal-overlay" onClick={onClose}>
      <div className="appointment-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="appointment-modal-header">
          <h3>Appointment Details</h3>
          <button className="modal-close-btn" onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        <div className="appointment-modal-info">
          <div className="modal-info-item">
            <span className="info-label">Patient:</span>
            <span className="info-value">{appointment?.name || 'N/A'}</span>
          </div>
          <div className="modal-info-item">
            <span className="info-label">Time:</span>
            <span className="info-value">{appointment?.time || 'N/A'}</span>
          </div>
          <div className="modal-info-item">
            <span className="info-label">Type:</span>
            <span className="info-value">{appointment?.type || 'N/A'}</span>
          </div>
        </div>

        <div className="appointment-modal-tabs">
         {/*  {userType === "Doctor" && (
            <>
              <button
                className={`tab-btn ${activeTab === 'observations' ? 'active' : ''}`}
                onClick={() => setActiveTab('observations')}>
                Observations
              </button>
              <button
                className={`tab-btn ${activeTab === 'prescription' ? 'active' : ''}`}
                onClick={() => setActiveTab('prescription')}>
                Prescription
              </button>
            </>
          )} */}
          <button
            className={`tab-btn ${activeTab === 'payments' ? 'active' : ''}`}
            onClick={() => setActiveTab('payments')}>
            Payments
          </button>
        </div>

        <div className="appointment-modal-body">
          {/* {activeTab === 'observations' && userType === "Doctor" && (
            <div className="tab-content">
              <h4>Observations</h4>
              {appointment?.questions && appointment.questions.length > 0 ? (
                <div className="observations-list">
                  {appointment.questions.map((q, idx) => (
                    <div key={idx} className="observation-item">
                      <strong>{q.label}:</strong> <span>{q.options_value || 'N/A'}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="empty-message">No observations recorded</p>
              )}
            </div>
          )}

          {activeTab === 'prescription' && userType === "Doctor" && (
            <div className="tab-content">
              <h4>Prescription</h4>
              <p className="empty-message">No prescription data available</p>
            </div>
          )} */}

          {activeTab === 'payments' && (
            <div className="tab-content" style={{ padding: '0' }}>
              {paymentLoading ? (
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  padding: '40px 20px',
                  flexDirection: 'column',
                  gap: '12px'
                }}>
                  <div style={{
                    width: '36px',
                    height: '36px',
                    border: '3px solid #f3f4f6',
                    borderTopColor: '#f59e0b',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }}></div>
                  <p style={{ color: '#6b7280', fontSize: '14px', margin: 0 }}>Chargement...</p>
                </div>
              ) : paymentError ? (
                <div style={{
                  padding: '30px 20px',
                  textAlign: 'center',
                  backgroundColor: '#fef2f2',
                  borderRadius: '8px',
                  margin: '16px'
                }}>
                  <p style={{ color: '#dc2626', fontSize: '14px', margin: 0 }}>{paymentError}</p>
                </div>
              ) : paymentData ? (
                <div style={{ padding: '16px' }}>
                  <PaymentDetailsTable
                    currentPayment={paymentData}
                    formatCurrency={(amount) => new Intl.NumberFormat('fr-DZ', { 
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    }).format(amount || 0) + ' DA'}
                    formatDate={(dateString) => new Date(dateString).toLocaleDateString('fr-FR', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                    getStatusBadge={(status) => {
                      const badges = {
                        paid: { label: 'Payé', className: 'success', icon: CheckCircle },
                        unpaid: { label: 'Impayé', className: 'error', icon: AlertCircle },
                        partial: { label: 'Partiel', className: 'warning', icon: AlertCircle }
                      };
                      const badge = badges[status] || badges.unpaid;
                      const Icon = badge.icon;
                      
                      return (
                        <span className={`pmt-badge ${badge.className}`}>
                          <Icon size={12} />
                          {badge.label}
                        </span>
                      );
                    }}
                    showActions={userType === "Doctor"}
                    onAddPayment={() => setShowAddPaymentModal(true)}
                    onAddService={() => setShowAddServiceModal(true)}
                    isLastAppointment={true}
                    isAppointmentPast={isAppointmentPast}
                  />
                </div>
              ) : (
                <div style={{
                  padding: '40px 20px',
                  textAlign: 'center',
                  backgroundColor: '#fffbeb',
                  margin: '16px',
                  borderRadius: '8px',
                  border: '1px dashed #fcd34d'
                }}>
                  <div style={{ fontSize: '32px', marginBottom: '8px', opacity: 0.6 }}>💳</div>
                  <p style={{ color: '#92400e', fontSize: '14px', margin: 0 }}>Aucune donnée de paiement</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Add Payment Modal */}
        {userType === "Doctor" && paymentData && (
          <AddPaymentModal
            show={showAddPaymentModal}
            onClose={() => setShowAddPaymentModal(false)}
            onConfirm={handleAddPayment}
            currentPayment={paymentData}
            statistics={{
              totalDebt: paymentData.debt,
              totalAmount: paymentData.totalAmount,
              paidAmount: paymentData.paidAmount
            }}
            formatCurrency={(amount) => new Intl.NumberFormat('fr-DZ', { 
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            }).format(amount || 0) + ' DA'}
          />
        )}

        {/* Add Service Modal */}
        {userType === "Doctor" && paymentData && (
          <AddServiceModal
            show={showAddServiceModal}
            onClose={() => setShowAddServiceModal(false)}
            onConfirm={handleAddService}
            serviceCatalog={serviceCatalog}
            currentPayment={paymentData}
            formatCurrency={(amount) => new Intl.NumberFormat('fr-DZ', { 
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            }).format(amount || 0) + ' DA'}
          />
        )}
      </div>
    </div>
  );
};

export default AppointmentDetailsModal;
